import Image from 'next/image'
import {FC, ReactNode} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {Modal} from '@/components/common/modal/Modal'
import {useSuccessPageContent} from '@/content/successPage'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {EmailClientLogos} from '../../common/success/EmailClientLogos'

export type SuccessModalContextProps = {
  title: string // must be already translated text
  // Must contain already translated text.
  // SuccessModal is rendered at the root, so use <Trans> with extreme care regarding
  // which translation namespaces are available in that provider scope.
  description: ReactNode
  image?: {src: string; alt: string} | undefined
  hasEmailClients?: boolean
  hasSpamDescription?: boolean
  buttons?: {
    text: string
    variant?: ButtonVariant
    onClick: () => void
  }[]
  maxContentWidth?: string
}

export type SuccessModalProps = SuccessModalContextProps & {
  onClose: () => void
  isVisible: boolean
}

export const SuccessModal: FC<SuccessModalProps> = ({
  title,
  description,
  image,
  hasEmailClients = false,
  hasSpamDescription = false,
  buttons,
  maxContentWidth,
  onClose,
  isVisible,
}) => {
  const {t} = useTranslation()
  const successContent = useSuccessPageContent()
  const imageSrc = image?.src || successContent.imageSrc
  const imageAlt = image?.alt || successContent.imageAlt

  const textColor = theme.palette.text.light

  return (
    <>
      <Modal isVisible={isVisible} closeModal={onClose}>
        <div className="content">
          <div className="image-wrapper">
            <Image
              src={imageSrc}
              alt={imageAlt}
              style={{objectFit: 'contain'}}
              fill
              sizes="104px"
            />
          </div>
          {title && (
            <Text variant={TextVariant.h2} align="center">
              {title}
            </Text>
          )}
          <div className="description">
            {typeof description === 'string' ? (
              <Text align="center" color={textColor}>
                {description}
              </Text>
            ) : (
              description
            )}
          </div>
          {hasEmailClients && (
            <div className="wrapper">
              <EmailClientLogos />
            </div>
          )}
          {hasSpamDescription && (
            <div className="wrapper">
              <Text align="center" fontWeight={TextFontWeight.bold} color={textColor}>
                {t(successContent.emailNotReceivedDescription)}
              </Text>
              <Text align="center" color={textColor}>
                {t(successContent.spamOrContactDescription)}
              </Text>
            </div>
          )}
          {buttons && buttons.length > 0 && (
            <div className="buttons">
              {buttons.map(({text, variant, onClick}) => (
                <div key={text} className="button">
                  <Button
                    type="button"
                    fullWidth
                    variant={variant ?? ButtonVariant.primary}
                    onClick={onClick}
                  >
                    {text}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      <style jsx>{`
        .content {
          max-width: ${maxContentWidth};
          min-width: ${maxContentWidth};
          padding: ${theme.spacing[3]};
          margin: 0 auto;
        }
        .image-wrapper {
          position: relative;
          width: 104px;
          height: 104px;
          margin: 0 auto ${theme.spacing[3.5]};
        }
        .description {
          margin: ${theme.spacing[2.5]} auto 0;
        }
        .buttons {
          text-align: center;
          margin-top: ${theme.spacing[5]};
        }
        .button + .button {
          margin-top: ${theme.spacing[2]};
        }
        .wrapper {
          margin-top: ${theme.spacing[3.5]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .content {
            padding: ${theme.spacing[3]} 0;
            max-width: 100%;
            min-width: inherit;
          }
        }
      `}</style>
    </>
  )
}
